import React from "react";
import { Lock, ArrowRight } from "lucide-react";

/*
 * Salary changes rejected on re-import.
 *
 * The employee import never changes the Net / Gross / Insurance salary of an
 * employee that already exists (only a brand-new employee, or one with no
 * salary on file yet, takes the salary from the sheet). Every other column of
 * those rows is still updated. Salary changes must go through
 * Changes → Bulk Actions (Salary Adjustment) so they are approved, audited
 * and pushed into payroll.
 *
 * Shared by ImportPreviewModal (before commit) and ImportResultsModal (after).
 */

const FIELD_LABELS = {
  netSalary: { en: "Net Salary", ar: "صافي الراتب" },
  grossSalary: { en: "Gross Salary", ar: "إجمالي الراتب" },
  insuranceSalary: { en: "Insurance Salary", ar: "الأجر التأميني" },
};

const fmtMoney = (v) =>
  v == null || v === ""
    ? "—"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const SalaryRejectionsNotice = ({ rejections, isRtl, isDark, fonts, colors, committed = false }) => {
  const list = Array.isArray(rejections) ? rejections : [];
  if (!list.length) return null;

  const amber = "#d97706";
  const cell = { padding: "8px 14px", fontSize: fonts.small, borderTop: `1px solid ${colors.border}` };

  return (
    <div data-testid="salary-rejections">
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <Lock size={15} color={amber} />
        <span style={{ fontSize: fonts.small, fontWeight: 700, color: amber }}>
          {isRtl
            ? `تم رفض تعديلات الراتب (${list.length})`
            : `Salary changes rejected (${list.length})`}
        </span>
      </div>

      <div
        style={{
          margin: "0 0 10px",
          padding: "10px 14px",
          borderRadius: "10px",
          background: isDark ? "rgba(217,119,6,0.10)" : "rgba(217,119,6,0.07)",
          border: "1px solid rgba(217,119,6,0.30)",
          fontSize: fonts.tiny,
          color: colors.text,
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {isRtl
            ? committed
              ? "لم يتم تغيير صافي الراتب أو إجمالي الراتب أو الأجر التأميني لهؤلاء الموظفين الحاليين. تم تحديث باقي الحقول بشكل طبيعي."
              : "لن يتم تغيير صافي الراتب أو إجمالي الراتب أو الأجر التأميني لهؤلاء الموظفين الحاليين. سيتم تحديث باقي الحقول بشكل طبيعي."
            : committed
              ? "Net Salary, Gross Salary and Insurance Salary were NOT changed for these existing employees. All other fields were updated normally."
              : "Net Salary, Gross Salary and Insurance Salary will NOT be changed for these existing employees. All other fields will be updated normally."}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 4, color: amber, fontWeight: 600 }}>
          {isRtl ? "لتعديل الراتب أو أي تغيير عليه استخدم" : "To update the salary or make any salary change, use"}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {isRtl ? "التغييرات" : "Changes"}
            <ArrowRight size={12} style={{ transform: isRtl ? "scaleX(-1)" : "none" }} />
            {isRtl ? "الإجراءات الجماعية (تعديل الراتب)" : "Bulk Actions (Salary Adjustment)"}
          </span>
        </div>
      </div>

      <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(217,119,6,0.25)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: isDark ? "rgba(217,119,6,0.08)" : "rgba(217,119,6,0.05)" }}>
              {[
                isRtl ? "الصف" : "Row",
                isRtl ? "الموظف" : "Employee",
                isRtl ? "الحقل" : "Field",
                isRtl ? "الحالي (محفوظ)" : "Current (kept)",
                isRtl ? "في الملف (مرفوض)" : "In file (rejected)",
              ].map((h, i) => (
                <th key={i} style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: amber, textAlign: isRtl ? "right" : "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.flatMap((r, ri) =>
              (r.fields || []).map((f, fi) => (
                <tr key={`${ri}-${fi}`} style={{ background: ri % 2 === 0 ? "transparent" : isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)" }}>
                  {fi === 0 && (
                    <td rowSpan={r.fields.length} style={{ ...cell, fontWeight: 700, color: amber }}>{r.row}</td>
                  )}
                  {fi === 0 && (
                    <td rowSpan={r.fields.length} style={{ ...cell, color: colors.text }}>
                      <div style={{ fontWeight: 600 }}>{r.employeeName || "—"}</div>
                      {r.clientCode && <div style={{ fontSize: fonts.tiny, color: colors.subtext }}>{r.clientCode}</div>}
                    </td>
                  )}
                  <td style={{ ...cell, fontWeight: 600, color: colors.text }}>
                    {(FIELD_LABELS[f.field] || {})[isRtl ? "ar" : "en"] || f.field}
                  </td>
                  <td style={{ ...cell, color: "#10b981", fontWeight: 600 }}>{fmtMoney(f.existing)}</td>
                  <td style={{ ...cell, color: "#ef4444", textDecoration: "line-through" }}>{fmtMoney(f.incoming)}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalaryRejectionsNotice;
